import React, { useContext, useState, useEffect } from "react";
import Img from "gatsby-image";
import styled from "@emotion/styled";
import { css } from "@emotion/core";
import { graphql, useStaticQuery } from "gatsby";
import Link from "@src/components/UTMLink";
import posed, { Transition } from "react-pose";
import Container from "./Container";
import { Box, Flex, FlexProps } from "./Boxes";
import Spacer from "./Spacer";
import Button from "./Button";
import BurgerButton from "./BurgerButton";
import Translate from "@src/components/translation/Translate";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { AppDataContext } from "./AppDataContext";
import { mediaQueries } from "@src/styles/theme";
import { getRelativePath } from "@src/utils/routes";
import { useLocation } from "@reach/router";
import isMobile from "@src/utils/isMobile";
import { logEvent } from "@src/utils/logClient";
import SchoolClosureBannerSection from "@src/components/partials/index/SchoolClosureBannerSection";
import window from "global/window";
import cookies from "cookies-js";
import { cookiesWork } from "@src/utils/cookies";
import { getSubdomainURL } from "@src/utils/getSubdomainURL";

const bottomBorder = css`
  border-bottom: 1px solid #eee;
`;

const Nav = styled.nav`
  position: fixed;
  position: fixed;
  background-color: white;
  right: 0;
  left: 0;
  z-index: 1030;
  flex: 0 0 auto;
`;

const LinkText = styled.span`
  color: #777;
  font-weight: 500;
  line-height: 22px;
  font-family: proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif;
  word-break: keep-all;
  white-space: nowrap;

  &:hover {
    color: #00bcf2;
    text-decoration: none;
  }
`;

const LoggedInText = styled.span`
  color: #00bcf2;
  font-weight: 600;
  ${mediaQueries[1]} {
    font-size: 18px;
  }
  line-height: 22px;
  font-family: proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif;
  word-break: keep-all;
  white-space: nowrap;

  &:hover {
    color: #00bcf2;
    color: #00a8d9;
    text-decoration: none;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  padding: 10px 15px;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const StyledGatsbyLink = StyledLink.withComponent(Link);
const StyledHTMLLink = StyledLink.withComponent("a");

const Logo = styled(Img)`
  width: 176px;
`;

const downloadButton = css`
  color: #777;
  font-weight: 500;
  margin-left: 0px;
  background-color: #fff;
  border: 1px solid #ccc;
  &:hover,
  &:focus {
    border: 1px solid #adadad;
  }
  ${mediaQueries[0]} {
    margin-left: 15px;
  }
`;

type NavLinkProps = FlexProps & {
  openLoginModal: () => void;
  openSignupModal: () => void;
  appData: any;
  onClick: () => void;
};
const NavLinks: React.FC<NavLinkProps> = ({ openLoginModal, openSignupModal, appData, ...props }) => {
  let loginPart = null;
  if (appData.loadingSession) {
    loginPart = (
      <>
        <StyledHTMLLink href="https://teach.classdojo.com/">
          <LinkText>
            <Translate path="layouts.main.teacher_login" />
          </LinkText>
        </StyledHTMLLink>
        <StyledHTMLLink href="https://home.classdojo.com/">
          <LinkText>
            <Translate path="layouts.main.parent_login" />
          </LinkText>
        </StyledHTMLLink>
      </>
    );
  } else {
    if (appData.type) {
      loginPart = (
        <StyledHTMLLink href={getSubdomainURL(appData.type)}>
          <LoggedInText>
            {appData.name ? (
              <Translate path="components.welcome_banner.welcome_back" subs={{ name: appData.name }} />
            ) : (
              <Translate path="components.welcome_banner.welcome_back_anon" />
            )}
            <span>! </span>
            <Translate path="components.welcome_banner.come_in" />
          </LoggedInText>
        </StyledHTMLLink>
      );
    } else {
      loginPart = (
        <>
          <StyledHTMLLink onClick={openLoginModal} data-test-name="open-login-modal">
            <LinkText>
              <Translate path="pages.home.log_in" />
            </LinkText>
          </StyledHTMLLink>
          <Spacer ml={15} />
          <Button onClick={openSignupModal} marginTop={["15px", "15px", 0]} minWidth="100px">
            <Translate path="pages.home.sign_up" />
          </Button>
        </>
      );
    }
  }

  return (
    <Flex alignItems="center" {...props}>
      <StyledGatsbyLink
        to={getRelativePath("/#LearnMore")}
        onClick={() => {
          logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.learn_more.tap" });
        }}
      >
        <LinkText>
          <Translate path="layouts.main.learnmore" />
        </LinkText>
      </StyledGatsbyLink>
      <StyledGatsbyLink
        to={getRelativePath("/schoolwide/")}
        onClick={() => {
          logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.schools.tap" });
        }}
      >
        <LinkText>
          <Translate path="layouts.main.schools" />
        </LinkText>
      </StyledGatsbyLink>
      <StyledGatsbyLink
        to={getRelativePath("/resources/")}
        onClick={() => {
          logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.resources.tap" });
        }}
      >
        <LinkText>
          <Translate path="layouts.main.support.resources" />
        </LinkText>
      </StyledGatsbyLink>
      {loginPart}
      {/* TODO extract this device checking to a central place */}
      {typeof window !== "undefined" && (
        <>
          {isMobile().apple.tablet && (
            <Button
              as="a"
              href="https://itunes.apple.com/us/app/classdojo/id552602056?mt=8"
              outline
              css={downloadButton}
              marginBottom="10px"
              marginTop={["30px", "10px"]}
            >
              <Translate path="header.nav.get_ipad_app" />
            </Button>
          )}
          {isMobile().apple.phone && (
            <Button
              as="a"
              href="https://itunes.apple.com/us/app/classdojo/id552602056?mt=8"
              outline
              css={downloadButton}
              marginBottom="10px"
              marginTop={["30px", "10px"]}
            >
              <Translate path="header.nav.get_iphone_app" />
            </Button>
          )}
          {isMobile().android.device && (
            <Button
              as="a"
              href="https://play.google.com/store/apps/details?id=com.classdojo.android"
              outline
              css={downloadButton}
              marginBottom="10px"
              marginTop={["30px", "10px"]}
            >
              <Translate path="header.nav.get_android_app" />
            </Button>
          )}
        </>
      )}
    </Flex>
  );
};

const AnimatedNavLinks = posed.div({
  enter: { height: "auto" },
  exit: { height: 0 },
  transition: { default: { ease: "tween", duration: 1000 } },
});

type HeaderProps = {
  showClosureBanner: boolean;
  hideNav: boolean;
  closeBannerFunction: () => void;
};

const Header: React.FC = ({ showClosureBanner, closeBannerFunction, hideNav }: HeaderProps) => {
  const data = useStaticQuery(graphql`
    query {
      logo: file(name: { eq: "classdojo_logo_176@2x" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
  `);

  const { logo } = data;

  const [headerOpen, setHeaderOpen] = useState<boolean>(false);
  const [showPixel, setShowPixel] = useState({ facebook: false, pinterest: false });
  const toggleHeader = () => setHeaderOpen(!headerOpen);

  const appData = useContext(AppDataContext);
  const modalContext = useContext(ModalContext);

  const searchParams = useLocation().search;

  function checkTrackingPixel() {
    if (!cookiesWork()) return;

    if (!cookies.get("attribution") && searchParams.match(/utm_campaign/) && searchParams.match(/utm_source/)) {
      const campaignSource = searchParams.match(/utm_source=[a-z]+/)[0].split("=")[1];
      cookies.set("attribution", campaignSource);
    }

    if (cookies.get("attribution") == "facebook" && !showPixel.facebook) setShowPixel({ ...showPixel, facebook: true });
    if (cookies.get("attribution") == "pinterest" && !showPixel.pinterest)
      setShowPixel({ ...showPixel, pinterest: true });
  }

  checkTrackingPixel();

  function openLoginModal() {
    logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.log_in.tap" });
    modalContext.showModal(ModalType.Login);
  }

  function openSignupModal() {
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.sign_up.tap",
      metadata: appData.data.featureFlags,
    });
    modalContext.showModal(ModalType.Signup);
  }

  return (
    <Nav role="navigation">
      <Box height="88px" css={bottomBorder}>
        <Container height="100%">
          <Flex flexDirection="row" alignItems="center" height="100%">
            <Flex alignItems="center" flexGrow="1">
              <a href={getRelativePath("/")}>
                <Logo fluid={logo.childImageSharp.fluid} loading="eager" />
              </a>
              {!hideNav && (
                <BurgerButton
                  ml="auto"
                  display={[null, null, "none"]}
                  onClick={toggleHeader}
                  backgroundVisible={headerOpen}
                />
              )}
            </Flex>
            {!hideNav && (
              <NavLinks
                display={["none", "none", "flex"]}
                openLoginModal={openLoginModal}
                openSignupModal={openSignupModal}
                appData={appData.data}
                onClick={() => setHeaderOpen(false)}
              />
            )}
          </Flex>
        </Container>
      </Box>
      {!hideNav && (
        <Transition>
          {headerOpen && (
            <AnimatedNavLinks
              key="navlinks"
              css={css`
                overflow: hidden;
                position: absolute;
                width: 100%;
                background: white;
                z-index: 100;
              `}
            >
              <NavLinks
                flexDirection="column"
                display={["flex", "flex", "none"]}
                py={3}
                css={bottomBorder}
                width="100%"
                openLoginModal={openLoginModal}
                openSignupModal={openSignupModal}
                appData={appData.data}
                onClick={() => setHeaderOpen(false)}
              />
            </AnimatedNavLinks>
          )}
        </Transition>
      )}
      {showPixel.facebook && (
        <img
          style={{ position: "absolute" }}
          height="1"
          width="1"
          src="https://www.facebook.com/tr?id=172748793147724&ev=PageView"
        />
      )}
      {showPixel.pinterest && (
        <img
          style={{ position: "absolute" }}
          height="1"
          width="1"
          alt=""
          src="https://ct.pinterest.com/v3/?tid=2612583504841&event=PageVisit&noscript=1"
        />
      )}
      {showClosureBanner ? <SchoolClosureBannerSection closeFunction={closeBannerFunction} /> : ""}
    </Nav>
  );
};

export default Header;
